import PropTypes from "prop-types";

import { useEffect, useState, useRef } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";

import { useQuery } from "@apollo/client";
import {
  AppBar,
  Link,
  Toolbar,
  Fab,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  MenuList,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FormattedMessage } from "react-intl";
import AddIcon from "@mui/icons-material/Add";
import FolderIcon from "@mui/icons-material/Folder";
import AssignmentIcon from "@mui/icons-material/Assignment";

import ContactsPopup from "../../Messaging/ContactsPopup";
import { ReactComponent as WorkspaceIconSVG } from "../../../assets/img/WorkspaceIconSmallFillToCurrentColor.svg";

import {
  cartInitialize,
  expandItem,
  imageBasketInitialize,
  setProductView,
  toggleCart,
  toggleDetails,
} from "../../../actions";

import {
  getCompanyLogo,
  getSelectedSiteOrderGroupId,
  getThemeName,
} from "../../../helpers/selectors";

import { getCart } from "../../../graphql/queries/getCart";
import { getImageBasket } from "../../../graphql/queries/getImageBasket";
import getProductView from "../../../graphql/queries/productView";
import { useMessagingContext } from "../../../hooks/Messaging";
import { useWorkspaceAdminContext } from "../../../hooks/useWorkspaceAdminContext";

import { Cart } from "../../Cart";
import { HeaderSearch, WorkspaceNew } from "../../common";
import SiteEventsWatcher from "../../SiteEventsWatcher";
import UserDetails from "../../UserDetails";

import NavbarMobileDrawer from "../NavBar/NavbarDrawer";
import MobileMenu from "../NavBar/NavbarMobile";

import PageRoute from "../../../constant/pageRouteDictionary.json";
import WorkspaceAdminProvider from "../../../context/WorkspaceAdminContext";

import {
  ImageBasketNotifyIcon,
  MessageNotifyIcon,
  ShoppingCartNotifyIcon,
} from "./NotifyIcons";

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    boxShadow: "unset",
    minHeight: 60,
  },
  header: {
    backgroundColor: theme.palette.component.headerBackground,
    color: theme.palette.component.headerColor,
    [theme.breakpoints.up("sm")]: {
      position: "fixed",
    },
    minHeight: 60,
    width: "100%",
    zIndex: 1100
  },
  toolbar: {
    minHeight: 60,
    padding: "3px 30px",
    [theme.breakpoints.down("sm")]: {
      padding: "15px 10px",
      flexWrap: "wrap",
      minHeight: 40,
    },
  },
  navbrandWrap: {
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  navbrand: {
    objectFit: "contain",
    maxWidth: 160,
    maxHeight: 58,
    borderRadius: 0,
    marginRight: "1em",
    [theme.breakpoints.down("sm")]: {
      width: 100,
      backgroundSize: "contain",
      marginRight: 8,
    },
  },
  notification: {
    display: "none",
    [theme.breakpoints.up("md")]: {
      display: "flex",
    },
  },
  sectionMobile: {
    display: "flex",
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },
  hideProfileOnMobile: {
    [theme.breakpoints.down("md")]: {
      // marginLeft: "auto",
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  /* --- Klunga: WorkspaceNew (flex) + plusknapp (auto) --- */
  leftCluster: {
    display: "flex",
    alignItems: "center",
    marginRight: "auto",   // tryck resten (sök/ikoner) åt höger
    minWidth: 0,
    gap: 8,
    flex: "0 1 30%",       // högst ~30% av vänsterytan
    maxWidth: "30%",
  },
  wsFill: {
    display: "flex",
    alignItems: "center",
    flex: "1 1 auto",      // Workspace fyller klungan
    minWidth: 0,
    "& > *": { width: "100%" }, // låt inre innehåll fylla wrappen, inte mer
  },

  /* --- Fab + meny --- */
  addFabWrapper: {
    display: "flex",
    alignItems: "center",
    position: "relative",
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  addFab: {
    width: 36,
    height: 36,
    minHeight: 36,
    boxShadow: "none",
    backgroundColor: "rgba(218,218,218,0.35)",
    color: "transparent",
    "&:hover": {
      color: "rgba(218,218,218,0.9)",
      backgroundColor: "rgba(218,218,218,0.5)",
    },
    "& svg": {
      width: 20,
      height: 20,
    },
    "& svg path": {
      fill: "transparent",
      // plustecknet tar samma färg som bakgrunden under knappen:
      stroke:
        (theme.palette.component && theme.palette.component.headerBackground)
          ? theme.palette.component.headerBackground
          : theme.palette.background.default,
      strokeWidth: 2,
      vectorEffect: "non-scaling-stroke",
    },
  },
  workspaceIconSVG: {
    //color: "rgba(0, 0, 0, 0.42)",
    flexShrink: 0,
    height: "0.93em",
    marginRight: 7,
  },
}));


const Header = (props) => {
  return (
    <WorkspaceAdminProvider>
      <WrappedHeader {...props} />
    </WorkspaceAdminProvider>
  );
}

const WrappedHeader = ({
  api,
  companyLogo,
  cartInitialize,
  cartItems,
  fileGroups,
  history,
  imageBasketInitialize,
  imageBasketItems,
  oneStreamGroups,
  orderGroupId,
  selectedWorkspace,
  setProductView,
  structure,
  toggleCart,
  toggleDetails,
  ui,
  workspaceName,
}) => {
  const classes = useStyles();

  const { unreadMessagesCount } = useMessagingContext();

  const company = useSelector(
    state => state.api.currentViewer.viewer.defaultTheme
  );

  const dispatch = useDispatch();

  const { data: imageBasketData, refetch: refetchImageBasket } = useQuery(
    getImageBasket,
    {
      variables: { orderGroupId: 0 },
    }
  );

  const { data, refetch: refetchGetCart } = useQuery(getCart, {
    skip: !selectedWorkspace.id,
  });
  const { data: productViewData, loading } = useQuery(getProductView, {
    variables: {
      siteId: selectedWorkspace.id,
    },
    skip: !selectedWorkspace.id,
  });

  // --- ADD MENU STATE ---

  const user = useSelector(state => state.api.currentViewer.viewer.username);
  const shouldShowAddButton = ["devops@kommunal.se",
    "anders.henriksson@kommunal.se",
    "jonas.eckhardt@kommunal.se",
    "burt.vonbolton@kommunal.se",
    "admin.uniqueue@uniqueue.se",
    "admin@uniqueue.se"].includes(user);

  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const addButtonRef = useRef(null);
  const handleToggleAddMenu = () => setAddMenuOpen(prev => !prev);
  const handleCloseAddMenu = (event) => {
    if (addButtonRef.current && addButtonRef.current.contains(event.target)) {
      return;
    }
    setAddMenuOpen(false);
  };
  const handleMenuItemClick = (cb) => {
    
    setAddMenuOpen(false);
    // behåll ingen funktionsändring här – koppla ev. navigation i appen själv
    // if (cb) cb();
  };

  const workspaceAdminContext = useWorkspaceAdminContext();

  const handleOpenWizard = () => {
    
    setAddMenuOpen(false);
    workspaceAdminContext.openCreateModal();
  }
  // --- SLUT ADD MENU ---

  const handleDeleteItem = async ({ index }) => {
    const cartItemsTemp = [...cartItems];
    cartItemsTemp.splice(index, 1);
    cartInitialize({
      items: cartItemsTemp,
    });
  };

  useEffect(() => {
    if (productViewData && !loading) {
      setProductView(productViewData);
    }
  }, [productViewData]);

  useEffect(() => {
    if (
      imageBasketData &&
      imageBasketData.imageBank &&
      imageBasketData.imageBank.items.length > 0
    ) {
      const { items } = imageBasketData.imageBank;
      imageBasketInitialize({ items });
    }
  }, [imageBasketData]);

  useEffect(() => {
    if (data && data.shoppingCartContent) {
      const { groups = [] } = data.shoppingCartContent || {};
      const orderGroup = groups.find(x => x.orderGroupId === orderGroupId);
      const { items = [], oneStreamGroups = [], fileGroups = [] } =
        orderGroup || {};

      cartInitialize({
        items,
        fileGroups,
        oneStreamGroups,
      });
    }
  }, [data, company]);

  const shoppingCartItemCount = selectedWorkspace.id ? (cartItems.length + fileGroups.length + oneStreamGroups.length) : 0;

  return (
    <div className={classes.root}>
      <AppBar position="relative" elevation={0} className={classes.header}>
        <Toolbar className={classes.toolbar}>
          <div className={classes.sectionMobile}>
            <NavbarMobileDrawer />
          </div>
          <Link href="/" className={classes.navbrandWrap}>
            <img src={companyLogo} alt="Logo" className={classes.navbrand} />
          </Link>

          {/* Klunga: WorkspaceNew (~30%) + plusknapp */}
          <div className={classes.leftCluster}>
            <div className={classes.wsFill}>
              <WorkspaceNew
                workspaceName={workspaceName}
                workspaces={structure}
              />
            </div>

            {shouldShowAddButton && (
              <div className={classes.addFabWrapper} ref={addButtonRef}>
                <Tooltip title={<FormattedMessage id="btn.add" />} arrow placement="right">
                  <Fab
                    aria-label={<FormattedMessage id="btn.add" />}
                    className={classes.addFab}
                    onClick={handleToggleAddMenu}
                    size="small"
                  >
                    <AddIcon />
                  </Fab>
                </Tooltip>

                <Popper
                  open={addMenuOpen}
                  anchorEl={addButtonRef.current}
                  role={undefined}
                  placement="bottom-start"
                  transition
                  disablePortal={false}
                  style={{ zIndex: 1300 }}
                >
                  {({ TransitionProps }) => (
                    <Grow {...TransitionProps} style={{ transformOrigin: "left top" }}>
                      <Paper elevation={6}>
                        <ClickAwayListener onClickAway={handleCloseAddMenu}>
                          <MenuList autoFocusItem={addMenuOpen} dense>
                            <MenuItem onClick={() => handleOpenWizard()} >
                              <ListItemIcon sx={{ minWidth: 30, mr: 0 }}>
                                <WorkspaceIconSVG className={classes.workspaceIconSVG} />
                              </ListItemIcon>
                              <ListItemText primary={<FormattedMessage id="admin.addNewWorkspace" />} />
                            </MenuItem>
                            <MenuItem onClick={() => handleMenuItemClick()}>
                              <ListItemIcon sx={{ minWidth: 30, mr: 0 }}>
                                <FolderIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary={<FormattedMessage id="projectSelector.newProject" />} />
                            </MenuItem>
                            <Divider />
                            <MenuItem onClick={() => handleMenuItemClick()}>
                              <ListItemIcon sx={{ minWidth: 30, mr: 0 }}>
                                <AssignmentIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary={<FormattedMessage id="project.taskContextMenu.addTask" />} />
                            </MenuItem>
                            <MenuItem onClick={() => handleMenuItemClick()}>
                              <ListItemIcon
                                sx={{
                                  minWidth: 30,
                                  mr: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <i
                                  className="fas fa-file-invoice"
                                  style={{
                                    fontSize: "18px",
                                    lineHeight: 1,
                                    width: "20px",
                                    textAlign: "left"
                                  }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <FormattedMessage id="admin.sales.invoices.buttons.newInvoice" />
                                }
                              />
                            </MenuItem>

                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </div>)}
          </div>

          <HeaderSearch
            toggleDetails={toggleDetails}
            searchDetails={ui.toggleDetails}
          />

          <div className={classes.notification}>
            <ContactsPopup>
              <MessageNotifyIcon counter={unreadMessagesCount} />
            </ContactsPopup>

            <ImageBasketNotifyIcon
              counter={imageBasketItems.length}
              onClick={() => history.push(PageRoute.IMAGE_BASKET)}
            />
            <ShoppingCartNotifyIcon
              counter={shoppingCartItemCount}
              onClick={() => toggleCart({ name: "Cart" })}
            />
          </div>

          {ui.toggleCart.open && (
            <Cart
              miniBasketIsOpen={ui.toggleCart.open}
              toggleCart={toggleCart}
              handleDeleteItem={handleDeleteItem}
              cartItems={cartItems}
              fileGroups={fileGroups}
              oneStreamGroups={oneStreamGroups}
              selectedWorkspace={selectedWorkspace}
              history={history}
            />
          )}

          <div className={classes.hideProfileOnMobile}>
            <UserDetails viewer={api.currentViewer.viewer} />
          </div>
          <MobileMenu
            totalCartItems={cartItems.length}
            totalImageItems={imageBasketItems.length}
            unreadMessagesCount={unreadMessagesCount}
          />
        </Toolbar>
        <SiteEventsWatcher />
      </AppBar>
    </div>
  );
};

const mapStateToProps = state => {
  return {
    api: state.api,
    cartItems: state.api.cart.cartItems,
    companyLogo: getCompanyLogo(state),
    fileGroups: state.api.cart.fileGroups,
    imageBasketItems: state.api.imageBasket.imageBasketItems,
    oneStreamGroups: state.api.cart.oneStreamGroups,
    orderGroupId: getSelectedSiteOrderGroupId(state),
    selectedWorkspace: state.ui.toggleWorkspaces.workspace,
    structure:
      state.api.currentViewer &&
        state.api.currentViewer.viewer &&
        state.api.currentViewer.viewer.structure &&
        state.api.currentViewer.viewer.structure.items &&
        state.api.currentViewer.viewer.structure.items.length > 0
        ? state.api.currentViewer.viewer.structure.items
        : [],
    themeName: getThemeName(state),
    ui: state.ui,
    workspaceName: state.ui.toggleWorkspaces.workspace.name,
    workspaces:
      state.api.currentViewer &&
        state.api.currentViewer.viewer &&
        state.api.currentViewer.viewer.sites
        ? state.api.currentViewer.viewer.sites
        : [],
  };
};

const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      cartInitialize,
      expandItem,
      imageBasketInitialize,
      setProductView,
      toggleCart,
      toggleDetails,
    },
    dispatch
  );
};

Header.defaultProps = {
  fileGroups: [],
  history: {
    push: () => {
      console.log("History is null");
    },
  },
  oneStreamGroups: [],
  orderGroupId: null,
  selectedWorkspace: {
    id: null,
    name: null,
  },
  workspaceName: null,
};

Header.propTypes = {
  api: PropTypes.shape({
    currentViewer: PropTypes.shape({
      viewer: PropTypes.shape({
        avatar: PropTypes.string,
        email: PropTypes.string,
        firstname: PropTypes.string,
        lastname: PropTypes.string,
      }),
    }),
  }).isRequired,
  cartInitialize: PropTypes.func.isRequired,
  cartItems: PropTypes.arrayOf(
    PropTypes.shape({
      productId: PropTypes.string,
      quantity: PropTypes.number,
    })
  ).isRequired,
  companyLogo: PropTypes.string.isRequired,
  expandItem: PropTypes.func.isRequired,
  fileGroups: PropTypes.arrayOf(
    PropTypes.shape({
      folderId: PropTypes.number,
      items: PropTypes.arrayOf(PropTypes.shape({})),
      packageId: PropTypes.string,
      packageName: PropTypes.string,
    })
  ),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  imageBasketInitialize: PropTypes.func.isRequired,
  imageBasketItems: PropTypes.arrayOf(
    PropTypes.shape({
      imageName: PropTypes.string,
      itemId: PropTypes.number,
      productId: PropTypes.string,
    })
  ).isRequired,
  oneStreamGroups: PropTypes.arrayOf(
    PropTypes.shape({
      items: PropTypes.arrayOf(PropTypes.shape({})),
      packageId: PropTypes.string,
      packageName: PropTypes.string,
    })
  ),
  orderGroupId: PropTypes.number,
  selectedWorkspace: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }),
  setProductView: PropTypes.func.isRequired,
  toggleCart: PropTypes.func.isRequired,
  toggleDetails: PropTypes.func.isRequired,
  ui: PropTypes.shape({
    toggleCart: PropTypes.shape({
      open: PropTypes.bool.isRequired,
    }).isRequired,
    toggleDetails: PropTypes.shape({
      showDetails: PropTypes.bool.isRequired,
    }).isRequired,
  }).isRequired,
  workspaceName: PropTypes.string,
};

export default connect(mapStateToProps, mapDispatchToProps)(Header);
